import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import type { MultiFactorError } from 'firebase/auth';
import {
  GIP_NAMES,
  GIP_NAMES_TRANSLATIONS,
} from 'frontend-login/utils/constant-values';
import {
  isMultiFactorError,
  type LoginResponse,
} from 'password-form/services/mfa-service';
import nextWithDefault from 'password-form/utils/auth-utils';
import { FIREBASE_ERROR_CODES } from 'password-form/utils/constant-values';

export default class LoginController extends Controller {
  queryParams = [
    'email',
    'next',
    'spiderMigrationMessage',
    'idp',
    'showGenericError',
    'continueLogin',
    'isLoginExpired',
  ];

  @service declare authentication: Services['authentication'];
  @service declare customFetch: Services['custom-fetch'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare sharedAuth: Services['shared-auth'];

  @tracked continueLogin = false;
  @tracked email = '';
  @tracked emailInputHasErrors = false;
  @tracked idp = null;
  @tracked isLoginExpired = false;
  @tracked isRequestRunning = false;
  @tracked next = '';
  @tracked showGenericError = false;
  @tracked spiderMigrationMessage = undefined;
  @tracked verificationNeeded = false;

  initializeErrorHandling = modifier((): void => {
    if (this.showGenericError) {
      this.sharedAuth.showErrorMessage();
    }
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormValid() {
    return this.sharedAuth.isEmailValid;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSubmitButtonDisabled() {
    return (
      this.isRequestRunning ||
      !this.sharedAuth.emailInputValue ||
      (this.shouldRenderPasswordInput && !this.sharedAuth.password)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get newPlatformConfirmationText() {
    if (!this.sharedAuth.oldGipName || !this.sharedAuth.activeGipName) {
      return '';
    }

    const oldPlatformText =
      this.sharedAuth.oldGipName === GIP_NAMES.PASSWORD
        ? this.intl.t('login.gip.email_and_password')
        : // Add fallback in case the translation is not available
          GIP_NAMES_TRANSLATIONS[this.sharedAuth.oldGipName] ||
          this.sharedAuth.oldGipName;

    let newPlatformText;
    if (this.sharedAuth.activeGipName === GIP_NAMES.PASSWORD) {
      newPlatformText = this.intl.t('login.gip.email_and_password');
    } else if (this.sharedAuth.activeLoginOption?.identity_provider.name) {
      // Use the name property for SAML providers (e.g. Lighthouse) and other providers that have it
      newPlatformText =
        this.sharedAuth.activeLoginOption.identity_provider.name;
    } else {
      // Fallback to translation or gip name (e.g. saml.lighthouse)
      newPlatformText =
        GIP_NAMES_TRANSLATIONS[this.sharedAuth.activeGipName] ||
        this.sharedAuth.activeGipName;
    }

    return this.intl.t('login.change_platform_message', {
      oldPlatform: oldPlatformText,
      newPlatform: newPlatformText,
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pageTitle() {
    return this.intl.t('login.sign_in');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shouldRenderPasswordInput() {
    return Boolean(
      this.sharedAuth.passwordLoginOption &&
      !this.sharedAuth.showSSOConfirmationScreen,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showLoadingScreen() {
    return this.sharedAuth.isRedirecting && !this.sharedAuth.loadingButtonType;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPasswordLogin() {
    return (
      this.sharedAuth.passwordLoginOption &&
      !this.sharedAuth.showSSOConfirmationScreen
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSubmitButton() {
    return (
      !this.sharedAuth.loginOptions ||
      this.sharedAuth.passwordLoginOption !== undefined
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get title() {
    if (this.verificationNeeded) {
      return this.intl.t('login.verify_account_title');
    }
    return this.intl.t('login.title');
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    if (localStorage.getItem('isLoginExpired')) {
      this.isLoginExpired = true;
      localStorage.removeItem('isLoginExpired');
    }
  }

  handleFirebaseError(error: MultiFactorError): void {
    if (error.code === FIREBASE_ERROR_CODES.MULTI_FACTOR_REQUIRED) {
      /**
       * We set the authenticated email here because even when the user is not completely authenticated,
       * we still need to know the email to continue the MFA process
       */
      this.sharedAuth.authenticatedEmail = this.sharedAuth.emailInputValue;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.sharedAuth.solveMfa(error);
      return;
    }

    if (error.code === FIREBASE_ERROR_CODES.INVALID_CREDENTIALS) {
      this.sharedAuth.showErrorMessage({
        message: this.intl.t('login.error.invalid_credentials'),
        title: '',
      });
      return;
    }

    this.sharedAuth.showErrorMessage();
    this.errorTracking.captureException(error);
  }

  async handleMigration(params: {
    next?: string;
  }): Promise<LoginResponse | null> {
    return this.customFetch.post('/sso/login-and-migrate/', params, {
      username: this.sharedAuth.emailInputValue,
      password: this.sharedAuth.password,
    });
  }

  async signInWithPassword(): Promise<void> {
    this.sharedAuth.trackSentryUser(this.sharedAuth.emailInputValue);

    try {
      this.isRequestRunning = true;

      if (!this.sharedAuth.passwordLoginOption) {
        throw new Error('No password login option available');
      }

      let params = {};
      if (this.next) {
        params = {
          next: nextWithDefault(this.next),
        };
      }

      // If the user is not migrated to the firebase authentication system, we need to migrate them first through UM
      if (!this.sharedAuth.passwordLoginOption.is_migrated) {
        this.sharedAuth.cachedMigrateResult =
          await this.handleMigration(params);

        /**
         * The fetch service doesn't throw an error when the response is not ok, so we need to check it manually
         * If the migration request fails, show an error message and handle the error
         */
        if (
          this.sharedAuth.cachedMigrateResult &&
          !this.sharedAuth.cachedMigrateResult.response.ok
        ) {
          this.sharedAuth.showErrorMessage();
          this.sharedAuth.handleErrors(
            this.sharedAuth.cachedMigrateResult,
            true,
          );
          this.isRequestRunning = false;
          return;
        }
      } else {
        // If the user is already connected to firebase, we can sign in directly
        this.sharedAuth.cachedCredential =
          await this.authentication.signInWithPassword(
            this.sharedAuth.emailInputValue,
            this.sharedAuth.password,
          );
      }

      this.sharedAuth.authenticatedEmail = this.sharedAuth.emailInputValue;
      await this.sharedAuth.completeLoginProcess(params);
    } catch (error: unknown) {
      if (isMultiFactorError(error)) {
        this.handleFirebaseError(error);
        return;
      }
      this.sharedAuth.showErrorMessage();
      this.errorTracking.captureException(error);
    } finally {
      this.isRequestRunning = false;
    }
  }

  /**
   * Connect with new platform
   *
   * `applyConnectWithNewPlatform`, `confirmNewPlatform`, and `cancelConnectWithNewPlatform`
   * are used when the user wants to change their login method, either by their own will or
   * by their administrator forcing them to use SSO.
   */
  @action
  applyConnectWithNewPlatform(): void {
    this.sharedAuth.connectWithNewPlatformConfirmation = false;
    this.sharedAuth.connectWithNewPlatformInProcess = true;
    this.verificationNeeded = true;
    this.submitForm();
  }

  @action
  cancelConnectWithNewPlatform(): void {
    this.sharedAuth.connectWithNewPlatformConfirmation = false;
  }

  @action
  confirmNewPlatform(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.sharedAuth.confirmNewPlatform();
  }

  @action
  denySSOConfirmation(): void {
    this.sharedAuth.showSSOConfirmationScreen = false;
    this.sharedAuth.showError = true;
    this.sharedAuth.errorMessage = this.intl.t('login.error.deny_sso');
  }

  @action
  reset(): void {
    this.sharedAuth.reset();
  }

  @action
  submitForm(): void {
    if (this.isFormValid) {
      this.sharedAuth.showError = false;
      this.sharedAuth.emailInputHasErrors = false;

      if (
        this.sharedAuth.isEmailValid &&
        !this.sharedAuth.userHasloginOptions
      ) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.sharedAuth.fetchLoginOptions(this.sharedAuth.emailInputValue);
      }

      if (
        this.sharedAuth.userHasloginOptions &&
        this.sharedAuth.password !== ''
      ) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.signInWithPassword();
      }
    }

    if (!this.sharedAuth.isEmailValid) {
      this.sharedAuth.emailInputHasErrors = true;
    }
  }

  @action
  submitFormWithEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !this.isSubmitButtonDisabled) {
      this.submitForm();
    }
  }
}
